import { useState, useEffect, useRef, useCallback } from "react";
import { Platform, ScanRequest } from "../types";

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;
const TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) || "";
const LONG_SCAN_THRESHOLD = 3;

const PLATFORM_OPTIONS: ReadonlyArray<{ value: Platform; label: string }> = [
  { value: "chesscom", label: "Chess.com" },
  { value: "lichess", label: "Lichess.org" },
  { value: "both", label: "Both" },
];

interface SearchFormInitialValues {
  username: string;
  platform: Platform;
  monthsBack: number;
}

interface SearchFormProps {
  onSubmit: (req: ScanRequest) => void;
  loading: boolean;
  /** Optional pre-fill values, typically read from URL query params. */
  initialValues?: SearchFormInitialValues;
}

export default function SearchForm({ onSubmit, loading, initialValues }: SearchFormProps) {
  const [username, setUsername] = useState(initialValues?.username ?? "");
  const [monthsBack, setMonthsBack] = useState(
    initialValues ? String(initialValues.monthsBack) : "3",
  );
  const [platform, setPlatform] = useState<Platform>(initialValues?.platform ?? "chesscom");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const [error, setError] = useState<string | null>(null);

  const renderWidget = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current || !window.turnstile) return;
    if (widgetIdRef.current !== null) return;
    widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => setTurnstileToken(token),
      "expired-callback": () => setTurnstileToken(null),
    });
  }, []);

  useEffect(() => {
    renderWidget();
    const interval = setInterval(() => {
      if (window.turnstile) {
        renderWidget();
        clearInterval(interval);
      }
    }, 200);
    return () => {
      clearInterval(interval);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget]);

  const monthsBackNum = parseInt(monthsBack, 10);
  const showLongScanWarning =
    Number.isInteger(monthsBackNum) && monthsBackNum > LONG_SCAN_THRESHOLD;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = username.trim();

    if (!trimmed) {
      setError("Username is required.");
      return;
    }

    if (trimmed.length < 3 || trimmed.length > 25) {
      setError("Username must be between 3 and 25 characters.");
      return;
    }

    if (!USERNAME_REGEX.test(trimmed)) {
      setError("Username can only contain letters, numbers, underscores, and hyphens.");
      return;
    }

    const months = parseInt(monthsBack, 10);
    if (!Number.isInteger(months) || months < 1 || months > 12) {
      setError("Months to look back must be a whole number between 1 and 12.");
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Waiting for bot verification. Please try again.");
      return;
    }

    setError(null);
    onSubmit({
      username: trimmed,
      months_back: months,
      platform,
      turnstile_token: turnstileToken || "",
    });

    setTurnstileToken(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="search-form">
      <div className="form-group">
        <label htmlFor="username">Username</label>
        <input
          id="username"
          type="text"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setError(null);
          }}
          placeholder="Enter chess username"
          maxLength={25}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="months-back">Months to look back (1–12)</label>
        <input
          id="months-back"
          type="number"
          inputMode="numeric"
          value={monthsBack}
          onChange={(e) => {
            setMonthsBack(e.target.value);
            setError(null);
          }}
          min={1}
          max={12}
        />
      </div>

      <div className="form-group">
        <span id="platform-label" className="form-label">
          Platform
        </span>
        <div
          className="platform-segmented"
          role="radiogroup"
          aria-labelledby="platform-label"
          data-active-index={PLATFORM_OPTIONS.findIndex((o) => o.value === platform)}
        >
          <span className="platform-segmented__indicator" aria-hidden="true" />
          {PLATFORM_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={platform === opt.value}
              className="platform-segmented__option"
              onClick={() => setPlatform(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {showLongScanWarning && (
        <div className="form-warning">
          Scanning {monthsBackNum} months can take a minute or more — please don't refresh
          while it's working.
        </div>
      )}

      <div ref={turnstileRef} />

      {error && <p className="form-error">{error}</p>}

      <button
        type="submit"
        disabled={loading || !username.trim() || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
      >
        Scan for Cheaters
      </button>
    </form>
  );
}
